import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { BreadcrumbItem, BreadcrumbsComponent } from '../../../../shared/components/breadcrumbs/breadcrumbs.component';
import { ReportType } from '../../core/report-types';
import { ClientDemographicsReportComponent } from '../../reports/client-demographics-report/client-demographics-report.component';
import { GeographicDistributionReportComponent } from '../../reports/geographic-distribution-report/geographic-distribution-report.component';
import { MediationReportComponent } from '../../reports/mediation-report/mediation-report.component';
import { OutcomesReportComponent } from '../../reports/outcomes-report/outcomes-report.component';
import { OutreachContactsReportComponent } from '../../reports/outreach-contacts-report/outreach-contacts-report.component';
import { ProgramSummaryReportComponent } from '../../reports/program-summary-report/program-summary-report.component';
import { ServicesProvidedReportComponent } from '../../reports/services-provided-report/services-provided-report.component';

interface ReportCatalogItem {
  type: ReportType;
  slug: string;
  title: string;
  description: string;
}

@Component({
  selector: 'app-reports',
  imports: [
    CommonModule,
    RouterLink,
    BreadcrumbsComponent,
    ProgramSummaryReportComponent,
    ClientDemographicsReportComponent,
    OutreachContactsReportComponent,
    ServicesProvidedReportComponent,
    MediationReportComponent,
    OutcomesReportComponent,
    GeographicDistributionReportComponent,
  ],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportsComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly selectedReportType = signal<ReportType | null>(null);

  readonly reportCatalog: ReportCatalogItem[] = [
    {
      type: 'program-summary',
      slug: 'program-summary',
      title: 'Program Summary Report',
      description:
        'Review program-level totals for individuals served, outreach, services, mediations, outcomes, and geography.',
    },
    {
      type: 'client-demographics',
      slug: 'client-demographics',
      title: 'Client Demographics Report',
      description:
        'Break down distinct clients served by race, ethnicity, gender, and ZIP Code for a selected period.',
    },
    {
      type: 'outreach-contacts',
      slug: 'outreach-contacts',
      title: 'Outreach Contacts Report',
      description:
        'Review the number, type, duration, and follow-up volume of outreach contacts.',
    },
    {
      type: 'services-provided',
      slug: 'services-provided',
      title: 'Services Provided Report',
      description:
        'Compare services provided, services completed, clients served, and service unit totals.',
    },
    {
      type: 'mediation',
      slug: 'mediation',
      title: 'Mediation Report',
      description:
        'Track mediations conducted and resolved during a selected program period.',
    },
    {
      type: 'outcomes',
      slug: 'outcomes',
      title: 'Outcomes Report',
      description:
        'Review closed and declined-closed outcomes achieved during a selected period.',
    },
    {
      type: 'geographic-distribution',
      slug: 'geographic-distribution',
      title: 'Geographic Distribution Report',
      description:
        'Show clients, cases, services, and outreach contacts by ZIP Code.',
    },
  ];

  readonly selectedReport = signal<ReportCatalogItem | null>(null);

  readonly breadcrumbs = signal<BreadcrumbItem[]>([
    { label: 'Home', route: '/home' },
    { label: 'Reports' },
  ]);

  constructor() {
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      const reportSlug = params.get('reportSlug');

      if (!reportSlug) {
        this.selectedReportType.set(null);
        this.selectedReport.set(null);
        this.breadcrumbs.set([
          { label: 'Home', route: '/home' },
          { label: 'Reports' },
        ]);
        return;
      }

      const report = this.reportCatalog.find((item) => item.slug === reportSlug) ?? null;
      if (!report) {
        void this.router.navigate(['/reports']);
        return;
      }

      this.selectedReportType.set(report.type);
      this.selectedReport.set(report);
      this.breadcrumbs.set([
        { label: 'Home', route: '/home' },
        { label: 'Reports', route: '/reports' },
        { label: report.title },
      ]);
    });
  }

}
